import { lazy, Suspense, useEffect, useState } from 'react'

import { cn } from '@/lib/utils'
import { CortadoIcon } from '@/components/shared/cortado-icon'

// three.js is heavy, so load the glass canvas client-only and lazily.
const GlassObject = lazy(() => import('@/components/canvasui/GlassObject'))

export function AuthPageShell({
  children,
  contentClassName,
}: {
  children: React.ReactNode
  contentClassName?: string
}) {
  return (
    <main className="flex min-h-svh w-full bg-background">
      <div className="absolute left-6 top-6 z-10 flex items-center gap-2 opacity-0 animate-fade-in-down">
        <CortadoIcon className="size-8" />
        <span className="display-heading font-semibold tracking-[-0.025em]">
          Cortado <span className="text-brand">Pro</span>
        </span>
      </div>

      <div className="flex flex-1 items-center justify-center px-6 pb-10 pt-24 md:p-10">
        <div
          className={cn('w-full max-w-sm opacity-0 animate-fade-in-up', contentClassName)}
          style={{ animationDelay: '50ms' }}
        >
          {children}
        </div>
      </div>

      <AuthFeaturePanel />
    </main>
  )
}

export function AuthLegalNotice() {
  return (
    <p className="mt-6 text-center text-xs text-muted-foreground">
      By logging in, you agree to our{' '}
      <a href="https://www.cortado.pro/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
        Terms of Service
      </a>{' '}
      and{' '}
      <a href="https://www.cortado.pro/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
        Privacy Policy
      </a>
      .
    </p>
  )
}

function AuthFeaturePanel() {
  const [mounted, setMounted] = useState(false)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)

  useEffect(() => {
    setMounted(true)
    setPrefersReducedMotion(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false)
  }, [])

  return (
    <div className="relative hidden flex-1 items-center justify-center overflow-hidden bg-background lg:flex">
      <div className="paper-grid absolute inset-0 opacity-60" aria-hidden="true" />

      <div className="absolute left-1/2 top-1/2 h-[420px] w-px -translate-x-1/2 -translate-y-1/2 border-l border-dashed border-border opacity-0 animate-fade-in-up animation-delay-100" aria-hidden="true" />

      <div className="relative flex flex-col items-center gap-2">
        <FeatureCard icon={<WebsiteFormIcon />}>Website Forms</FeatureCard>
        <FeatureCard icon={<LeadNurturingIcon />} delay="animation-delay-150">
          Lead Nurturing Tools
        </FeatureCard>

        <div className="relative opacity-0 animate-fade-in-up animation-delay-200">
          {mounted && !prefersReducedMotion ? (
            <Suspense fallback={<CortadoIcon className="size-44" />}>
              <div className="size-44">
                <GlassObject
                  src="/favicon.svg"
                  className="size-full"
                  background=""
                  tint="#E6B340"
                  highlight="#E6B340"
                  ior={1.6}
                  thickness={3}
                  tintDensity={0.55}
                  environmentIntensity={1.1}
                  roughness={0.14}
                  dispersion={1.1}
                  scale={3.6}
                  floatIntensity={0}
                  rotationIntensity={0}
                  autoRotate={false}
                  orbit
                  zoom={false}
                />
              </div>
            </Suspense>
          ) : (
            <CortadoIcon className="size-44" />
          )}
        </div>

        <FeatureCard icon={<EventManagementIcon />} delay="animation-delay-250">
          Event Management
        </FeatureCard>
        <FeatureCard icon={<PaymentProcessingIcon />} delay="animation-delay-300">
          Payment Processing
        </FeatureCard>
      </div>
    </div>
  )
}

function FeatureCard({
  children,
  icon,
  delay = 'animation-delay-100',
}: {
  children: React.ReactNode
  icon: React.ReactNode
  delay?: string
}) {
  return (
    <div className={cn('flex w-56 items-center gap-3 rounded-md border border-border bg-card px-4 py-3 opacity-0 shadow-sm animate-fade-in-up', delay)}>
      <div className="flex size-10 items-center justify-center rounded-lg border border-border bg-elevated">
        {icon}
      </div>
      <span className="text-sm font-medium text-foreground">{children}</span>
    </div>
  )
}

function WebsiteFormIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-5 text-brand">
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  )
}

function LeadNurturingIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-5 text-brand">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  )
}

function EventManagementIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-5 text-brand">
      <path d="m3 11 18-5v12L3 14v-3z" />
      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
    </svg>
  )
}

function PaymentProcessingIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-5 text-brand">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <line x1="9" x2="15" y1="10" y2="10" />
    </svg>
  )
}
